import type { Item } from '@/lib/api/types/items';

/**
 * Extracts a field value from an item for color mapping purposes.
 *
 * This utility handles extraction of both built-in fields (status, team, parentItem, createdBy)
 * and custom fields (picklist, multi-picklist, etc).
 *
 * Note: Multi-select fields are not supported in "Color By" dropdown, so this
 * function returns only the first value for multi-picklist fields.
 *
 * @param item - The item to extract the field value from
 * @param fieldId - The ID of the field to extract (e.g., 'status', 'team', 'customfield_123')
 * @returns The field value as a string, or undefined if the field doesn't exist or has no value
 *
 * @example
 * ```typescript
 * const statusValue = getItemFieldValue(item, 'status'); // Returns: "1" (itemStatusId as string)
 * const teamValue = getItemFieldValue(item, 'team'); // Returns: "5" (teamId as string)
 * const customValue = getItemFieldValue(item, 'customfield_123'); // Returns picklist value
 * const createdByValue = getItemFieldValue(item, 'createdBy'); // Returns user ID string
 * ```
 */
export function getItemFieldValue(item: Item, fieldId: string): string | undefined {
  // Handle built-in fields
  if (fieldId === 'status') {
    return item.itemStatusId?.toString();
  }

  if (fieldId === 'team') {
    return item.teamId?.toString();
  }

  if (fieldId === 'parentItem') {
    return item.parentId?.toString();
  }

  if (fieldId === 'createdBy') {
    return item.createdBy;
  }

  // Handle custom fields
  if (item.customFields && fieldId in item.customFields) {
    const customFieldValue = item.customFields[fieldId];

    // Handle single picklist
    if (
      customFieldValue &&
      typeof customFieldValue === 'object' &&
      'PICKLIST' in customFieldValue
    ) {
      return String(customFieldValue.PICKLIST);
    }

    // Handle multi-picklist (array of values) - use first value for color mapping
    if (Array.isArray(customFieldValue) && customFieldValue.length > 0) {
      const firstValue = customFieldValue[0];
      if (firstValue && typeof firstValue === 'object' && 'MULTI_PICKLIST' in firstValue) {
        return String(firstValue.MULTI_PICKLIST);
      }
    }
  }

  return undefined;
}
